import { BaseResponseSchema, getRequiredMessage } from '@monetize/types/common';
import { number, object, string, z } from 'zod';
import {
  AmountUnitTypeEnum,
  DiscountCategoryEnumZ,
  DiscountLifeCyclePolicyEnumZ,
  DiscountStatusEnumZ,
  DiscountTypeEnumZ,
} from './enums.types';

// from GET /api/discounts/{discountId}
export const DiscountSchema = z.object({
  id: z.string().optional(),
  name: z.string().max(40).nonempty(getRequiredMessage('Name')),
  description: z
    .string()
    .max(255, {
      error: ' Description must be at most 255 characters long',
    })
    .nullish(),
  status: DiscountStatusEnumZ,
  discountType: DiscountTypeEnumZ.optional(),
  discountCode: z.string().max(80).nullish(),
  discountAmount: z
    .union([
      z.number({
        error: getRequiredMessage('Amount'),
      }),
      z.string(),
      z.null(),
    ])
    .transform((val) => (val === null || val === '' ? null : Number(val))),
  durationType: z.string().optional(),
  discountName: z.string().optional(),
  discountAmountOrPercent: z.number().optional(),
  durationMonths: z.number().nullish(),
  maxUses: z.number().nullish(),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
  autoRemove: z.boolean(),
  scopeOfferingId: z.string().nullish(),
  scopeProductId: z.string().nullish(),
  scopeProductIds: z.array(z.string()).nullish(),
  scopeRateIds: z.array(z.string()).nullish(),
  accountId: z.string().nullish(),
  excludeFromRenewal: z.boolean().nullish(),
  lifecyclePolicies: z.array(DiscountLifeCyclePolicyEnumZ).optional(),
});

export type IDiscountSchema = z.infer<typeof DiscountSchema>;

export const QuoteItemDiscountSchema = BaseResponseSchema.extend({
  discountId: z.string().nonempty(getRequiredMessage('discountId')),
  discountName: z.string().nonempty(getRequiredMessage('discountName')),
  discountType: DiscountTypeEnumZ,
  discountAmountOrPercent: z.number().nonnegative().nullish(),
  discountAmount: z.number().nonnegative().nullish(),
  discountLifecyclePolicies: z.array(DiscountLifeCyclePolicyEnumZ).optional(),
});

export type IQuoteItemDiscountSchema = z.infer<typeof QuoteItemDiscountSchema>;

export interface IDiscountInput {
  discountType: AmountUnitTypeEnum;
  discountAmount: number;
}

// from GET /api/discounts:
export const DiscountRespSchema = BaseResponseSchema.extend({
  name: string(),
  description: string(),
  discountType: DiscountTypeEnumZ,
  status: DiscountStatusEnumZ,
  discountCode: string(),
  quoteId: string(),
  discountAmount: number(),
  currency: string(),

  durationType: string(),
  durationMonths: number(),
  maxUses: number(),
  remainingUses: number(),
  startDate: string(),
  endDate: string(),
  autoRemove: z.boolean(),
  accountId: string(),
  scopeProductId: string(),
  scopeProductIds: z.array(z.string()).nullish(),
  scopeOfferingId: string(),
  scopeRateIds: z.array(z.string()).nullish(),
  locked: z.boolean(),
  excludeFromRenewal: z.boolean(),
  lifecyclePolicies: z.array(DiscountLifeCyclePolicyEnumZ),
  category: DiscountCategoryEnumZ.nullish(),
});

export type IDiscountRespSchema = z.infer<typeof DiscountRespSchema>;
export type IDiscount = IDiscountRespSchema;

export const ApplicableItemDiscountsSchema = object({
  id: string(),
  discount: DiscountSchema,
  applicableDiscountType: DiscountTypeEnumZ,
  applicableDiscountAmount: number(),
});

export type IApplicableItemDiscountsSchema = z.infer<
  typeof ApplicableItemDiscountsSchema
>;
